import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, PaletteMode } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Contexts
import { AuthProvider } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UsersList from './pages/users/UsersList';
import UserDetails from './pages/users/UserDetails';
import UserCreate from './pages/users/UserCreate';
import PromoCodesList from './pages/promocodes/PromoCodesList';
import PromoCodeDetails from './pages/promocodes/PromoCodeDetails';
import NotFound from './pages/NotFound';

// Components
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './layouts/MainLayout';

// Interface pour le contexte de thème
interface ThemeContextData {
  mode: PaletteMode;
  toggleMode: () => void;
}

export const ThemeContext = React.createContext<ThemeContextData>({
  mode: 'light',
  toggleMode: () => {},
});

// Création du thème modernisé
const createModernTheme = (mode: PaletteMode) => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#129baf',
        light: '#30b3c5',
        dark: '#0e7989',
      },
      secondary: {
        main: '#e99121',
        light: '#ffaa47',
        dark: '#d17a10',
      },
      background: {
        default: mode === 'light' ? '#f0f4f8' : '#0a1628',
        paper: mode === 'light' ? '#ffffff' : '#1a2744',
      },
      text: {
        primary: mode === 'light' ? '#00355f' : '#e0e0e0',
        secondary: mode === 'light' ? '#64748b' : '#94a3b8',
      },
      success: {
        main: '#2ecc71',
        light: '#58d68d',
        dark: '#27ae60',
      },
      error: {
        main: '#e74c3c',
        light: '#ec7063',
        dark: '#c0392b',
      },
      warning: {
        main: '#f39c12',
        light: '#f7dc6f',
        dark: '#d68910',
      },
      info: {
        main: '#3498db',
        light: '#85c1e9',
        dark: '#2980b9',
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      '0 3px 6px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.18)',
      '0 6px 12px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.12)',
      '0 8px 16px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.12)',
      '0 10px 20px rgba(0,0,0,0.12), 0 6px 12px rgba(0,0,0,0.12)',
      '0 12px 24px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.12)',
      '0 14px 28px rgba(0,0,0,0.12), 0 10px 20px rgba(0,0,0,0.12)',
      '0 16px 32px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.12)',
      '0 20px 40px rgba(0,0,0,0.12), 0 14px 28px rgba(0,0,0,0.12)',
      '0 24px 48px rgba(0,0,0,0.12), 0 16px 32px rgba(0,0,0,0.12)',
      '0 28px 56px rgba(0,0,0,0.12), 0 20px 40px rgba(0,0,0,0.12)',
      '0 32px 64px rgba(0,0,0,0.12), 0 24px 48px rgba(0,0,0,0.12)',
      '0 36px 72px rgba(0,0,0,0.12), 0 28px 56px rgba(0,0,0,0.12)',
      '0 40px 80px rgba(0,0,0,0.12), 0 32px 64px rgba(0,0,0,0.12)',
      '0 44px 88px rgba(0,0,0,0.12), 0 36px 72px rgba(0,0,0,0.12)',
      '0 48px 96px rgba(0,0,0,0.12), 0 40px 80px rgba(0,0,0,0.12)',
      '0 52px 104px rgba(0,0,0,0.12), 0 44px 88px rgba(0,0,0,0.12)',
      '0 56px 112px rgba(0,0,0,0.12), 0 48px 96px rgba(0,0,0,0.12)',
      '0 60px 120px rgba(0,0,0,0.12), 0 52px 104px rgba(0,0,0,0.12)',
      '0 64px 128px rgba(0,0,0,0.12), 0 56px 112px rgba(0,0,0,0.12)',
      '0 68px 136px rgba(0,0,0,0.12), 0 60px 120px rgba(0,0,0,0.12)',
      '0 72px 144px rgba(0,0,0,0.12), 0 64px 128px rgba(0,0,0,0.12)',
      '0 76px 152px rgba(0,0,0,0.12), 0 68px 136px rgba(0,0,0,0.12)',
      '0 80px 160px rgba(0,0,0,0.12), 0 72px 144px rgba(0,0,0,0.12)',
    ] as any,
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(18, 155, 175, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(18, 155, 175, 0.4)',
              transform: 'translateY(-2px)',
            },
          },
          outlined: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light'
              ? '0 4px 16px rgba(18, 155, 175, 0.08)'
              : '0 4px 16px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(18, 155, 175, 0.15)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(135deg, #129baf 0%, #0e7989 100%)',
            boxShadow: '0 4px 16px rgba(18, 155, 175, 0.3)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#129baf',
              },
              '&.Mui-focused': {
                borderColor: '#129baf',
                boxShadow: '0 0 0 3px rgba(18, 155, 175, 0.1)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
      },
    },
  });
};

function App() {
  const [mode, setMode] = useState<PaletteMode>('light');

  const theme = useMemo(() => createModernTheme(mode), [mode]);

  const toggleMode = () => {
    setMode((prevMode) => prevMode === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={
                <PrivateRoute>
                  <MainLayout />
                </PrivateRoute>
              }>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />

                <Route path="users">
                  <Route index element={<UsersList />} />
                  <Route path="new" element={<UserCreate />} />
                  <Route path=":id" element={<UserDetails />} />
                </Route>

                <Route path="promocodes">
                  <Route index element={<PromoCodesList />} />
                  <Route path=":id" element={<PromoCodeDetails />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
